import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, ShieldCheck } from 'lucide-react';
import { useAuth } from '../components/auth/AuthContext';
import { getMyCertificates } from '../lib/certificates';
import type { Certificate } from '../types/assess';
import { PageHeader, Panel, Stamp } from '../components/lms/ui';
import { CertificatesSkeleton } from '../components/lms/Skeletons';
import logo from '../components/assets/logo.png';

const Certificates: React.FC = () => {
  const { user } = useAuth();
  const [certs, setCerts] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [printingId, setPrintingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    void getMyCertificates(user.id)
      .then((c) => {
        // attach student name for print
        setCerts(c.map((x) => ({ ...x, student_name: user.name })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (!printingId) return;
    const reset = () => setPrintingId(null);
    window.addEventListener('afterprint', reset);
    const timer = window.setTimeout(() => window.print(), 60);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', reset);
    };
  }, [printingId]);

  const print = (id: string) => setPrintingId(id);

  if (loading) {
    return <CertificatesSkeleton />;
  }

  return (
    <div>
      <PageHeader
        title="Certificates"
        lede="Issued automatically when every lesson in a programme is cleared. Each certificate carries a code employers can check in the public register."
        actions={
          <Link to="/verify" className="lms-btn-secondary">
            Open verification register <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        }
      />

      {certs.length === 0 ? (
        <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
          <Panel>
            <div className="px-6 py-14 text-center">
              <Stamp tone="pending">Not issued</Stamp>
              <p className="mt-4 text-[16px] font-semibold text-ink">No certificate on file yet</p>
              <p className="mx-auto mt-1.5 max-w-[48ch] text-[13px] leading-5 text-ink-2">
                A certificate is issued when all lessons in an enrolled programme are marked complete. Revoked
                enrolments do not issue certificates.
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <Link to="/dashboard" className="lms-btn-primary">Open student overview</Link>
                <Link to="/courses" className="lms-btn-secondary">Browse programmes</Link>
              </div>
            </div>
          </Panel>
          <Panel>
            <div className="border-b border-rule px-5 py-3.5">
              <h2 className="text-[13px] font-semibold text-ink">After issue</h2>
            </div>
            <ul className="divide-y divide-rule text-[12px] leading-5 text-ink-2">
              <li className="px-5 py-3.5">Print or save the certificate as a PDF.</li>
              <li className="px-5 py-3.5">Send an employer the public verification code.</li>
              <li className="px-5 py-3.5">Revoked records show as revoked in the public register.</li>
            </ul>
          </Panel>
        </div>
      ) : (
        <div className="mt-8">
          <div className="flex flex-wrap items-end justify-between gap-3 border-b border-rule pb-3">
            <p className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-ink-2">
              {certs.length} {certs.length === 1 ? 'certificate' : 'certificates'} on file
            </p>
            <p className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-ink-2">Lagos / 2026 session</p>
          </div>

          <div className="mt-5 space-y-8">
            {certs.map((c) => (
              <article key={c.id} className="overflow-hidden rounded-2xl border border-rule bg-white shadow-[0_18px_50px_-42px_rgba(15,40,90,0.35)]">
                {c.revoked && (
                  <div className="flex flex-wrap items-center gap-3 border-b border-danger/25 bg-danger-soft px-5 py-3">
                    <Stamp tone="held">Revoked</Stamp>
                    <p className="text-[12.5px] leading-5 text-danger">
                      This certificate will not verify publicly
                      {c.revoked_reason ? ` — ${c.revoked_reason}` : ''}.
                    </p>
                  </div>
                )}

                {/* Printable certificate */}
                <div
                  className={`print-area px-4 py-6 sm:px-7 sm:py-8 ${printingId === c.id ? 'is-printing' : ''}`}
                  aria-label={`Certificate for ${c.student_name || user?.name} in ${c.course_title || c.course_id}`}
                >
                  <div className="border border-pine/25 bg-white">
                    <div className="border border-rule/80 p-6 text-center sm:p-10">
                      <div className="flex flex-col items-center justify-between gap-4 border-b border-rule pb-5 sm:flex-row sm:text-left">
                        <div className="flex items-center gap-3">
                          <span className="flex h-12 w-12 flex-none items-center justify-center border border-rule bg-paper p-1.5">
                            <img src={logo} alt="" className="h-full w-full object-contain" />
                          </span>
                          <span className="text-left">
                            <span className="block text-[12px] font-semibold uppercase tracking-[0.16em] text-pine">
                              Lagos School of Export Management
                            </span>
                            <span className="mt-0.5 block font-mono text-[8.5px] uppercase tracking-[0.13em] text-ink-2">
                              Lekki, Lagos, Nigeria · Certificate register
                            </span>
                          </span>
                        </div>
                        <Stamp tone={c.revoked ? 'held' : 'issued'}>{c.revoked ? 'Revoked' : 'Issued'}</Stamp>
                      </div>

                      <p className="mt-8 font-mono text-[9px] uppercase tracking-[0.24em] text-ink-2">
                        Certificate of completion
                      </p>
                      <p className="mt-4 text-[12.5px] text-ink-2">This certifies that</p>
                      <p className="mx-auto mt-2 max-w-[24ch] text-[clamp(1.6rem,3.2vw,2.4rem)] font-semibold leading-tight tracking-[-0.025em] text-ink">
                        {c.student_name || user?.name || 'Student'}
                      </p>
                      <p className="mt-4 text-[12.5px] text-ink-2">has successfully completed</p>
                      <p className="mx-auto mt-2 max-w-[34ch] text-[clamp(1.15rem,2.2vw,1.55rem)] font-semibold leading-snug tracking-[-0.015em] text-pine">
                        {c.course_title || c.course_id}
                      </p>

                      <div className="mx-auto mt-8 grid max-w-[600px] gap-3 border-y border-rule py-4 text-left sm:grid-cols-2">
                        <div>
                          <p className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-ink-2">Issued</p>
                          <p className="mt-1 text-[12px] font-medium text-ink">
                            {new Date(c.issued_at).toLocaleDateString('en-NG', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                        <div className="sm:text-right">
                          <p className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-ink-2">Verification code</p>
                          <p className="mt-1 font-mono text-[12px] font-semibold tracking-[0.08em] text-ink">{c.code}</p>
                        </div>
                      </div>

                      <div className="mt-6 flex flex-col items-center justify-between gap-3 text-left sm:flex-row">
                        <p className="max-w-[46ch] font-mono text-[8.5px] uppercase leading-4 tracking-[0.08em] text-ink-2">
                          Verify this record at lsem.org.ng/verify/{c.code}
                        </p>
                        <span className="inline-flex rotate-[-2deg] items-center border-2 border-pine/60 px-3 py-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-pine/80">
                          LSEM · Record
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 border-t border-rule px-5 py-4 print:hidden">
                  <button onClick={() => print(c.id)} className="lms-btn-primary !py-2 !text-[12px]">
                    <Download className="h-4 w-4" strokeWidth={1.5} /> Print / Save PDF
                  </button>
                  <Link to={`/verify/${c.code}`} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-pine hover:underline">
                    <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} /> Open public record · {c.code}
                  </Link>
                  {c.revoked && (
                    <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.12em] text-danger">
                      Record withheld from public verification
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @media print {
          @page { margin: 12mm; }
          body * { visibility: hidden; }
          .print-area { display: none; }
          .print-area.is-printing,
          .print-area.is-printing * { visibility: visible; }
          .print-area.is-printing {
            display: block;
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            margin: 0;
            padding: 0;
          }
          .print-area.is-printing .border { border-color: #D2CFC2 !important; }
        }
      `}</style>
    </div>
  );
};

export default Certificates;
